import { useEffect, useState } from "react";
import { hasElements } from "utils/arrays";
import { dateToString, getDateFromString, getDuration, getIntervalInBestUnit } from "utils/dateUtils";
import { getAttributeValue, hasAttributes } from "utils/objects";

const EVENT_COLORS = {
    1: { background: "#7986cb", foreground: "#ffffff" }, // Lavanda
    2: { background: "#33b679", foreground: "#ffffff" }, // Salvia
    3: { background: "#8e24aa", foreground: "#ffffff" }, // Uva
    4: { background: "#e67c73", foreground: "#ffffff" }, // Flamenco
    5: { background: "#f6c026", foreground: "#000000" }, // Plátano
    6: { background: "#f5511d", foreground: "#ffffff" }, // Mandarina
    7: { background: "#039be5", foreground: "#ffffff" }, // Pavo real
    8: { background: "#616161", foreground: "#ffffff" }, // Grafito
    9: { background: "#3f51b5", foreground: "#ffffff" }, // Arándano
    10: { background: "#0b8043", foreground: "#ffffff" }, // Albahaca
    11: { background: "#d50000", foreground: "#ffffff" }, // Tomate
};

const DEFAULT_PERMISSIONS = {
    GUESTS_CAN_INVITE_OTHERS: true,
    GUESTS_CAN_MODIFY: false,
    GUESTS_CAN_SEE_OTHER_GUESTS: true,
};

const getEventColor = (event) => {
    if (!event?.colorId) return null;
    return { background: EVENT_COLORS[event.colorId].background };
};

export const useEventUtils = ({ users = [], calendars = [], user, selectedCalendars = [] }) => {
    const [inUseCalendarColors, setInUseCalendarColors] = useState([]);

    useEffect(() => {
        const inUseColors = [];
        if (!hasElements(selectedCalendars)) return;
        selectedCalendars.forEach((calendar) => {
            inUseColors.push(calendar.backgroundColor);
        });
        setInUseCalendarColors(inUseColors);
    }, [selectedCalendars]);

    if (!hasElements(users) || !hasElements(calendars) || !hasAttributes(user)) {
        return {
            fromGoogleEvent: () => {},
            new: () => {},
            styleGetter: () => {},
            toGoogleEvent: () => {},
        };
    }

    let primaryCalendar;
    if (hasElements(selectedCalendars)) {
        primaryCalendar = selectedCalendars.find((x) => x.primary);
        if (!primaryCalendar) primaryCalendar = selectedCalendars.find((x) => ["owner", "writer"].includes(x.accessRole));
    }
    if (!primaryCalendar) primaryCalendar = calendars.find((x) => x.primary);
    if (!primaryCalendar) primaryCalendar = calendars.find((x) => ["owner", "writer"].includes(x.accessRole));

    let defaultEmailReminderMinutes = 10;
    if (hasElements(primaryCalendar.defaultReminders)) {
        const emailReminder = primaryCalendar.defaultReminders.find((x) => x.method === "email");
        if (emailReminder) defaultEmailReminderMinutes = emailReminder.minutes;
    }

    return {
        fromGoogleEvent: (googleEvent) => {
            const googleCalendar = calendars.find((x) => x.id === googleEvent.calendarId);

            const basicData = {
                allDay: Boolean(googleEvent.start.date) && Boolean(googleEvent.end.date),
                start: new Date(googleEvent.start.dateTime || getDateFromString(googleEvent.start.date, "yyyy-MM-DD")),
                end: new Date(googleEvent.end.dateTime || getDateFromString(googleEvent.end.date, "yyyy-MM-DD")),
                id: googleEvent.id,
                calendar: googleCalendar,
                accessRole: googleEvent.accessRole,
            };

            if (
                googleEvent.accessRole === "freeBusyReader" ||
                (googleEvent.accessRole === "reader" && googleEvent.visibility === "private")
            ) {
                let eventColor = null;
                // If there's a calendar we take the color from it, otherwise it means that this is an event from other guests and the color will be set randomly later.
                if (googleCalendar) {
                    eventColor = googleCalendar.backgroundColor;
                }
                return { ...basicData, title: "Busy", going: true, eventColor };
            }

            let notificationMinutes = defaultEmailReminderMinutes;
            if (googleEvent.reminders?.useDefault) notificationMinutes = defaultEmailReminderMinutes;
            else notificationMinutes = googleEvent.reminders?.overrides?.find((x) => x.method === "email")?.minutes;
            const intervalInBestUnit = getIntervalInBestUnit(notificationMinutes);
            const attendeeUser = googleEvent.attendees?.find((x) => x.email === user.email);
            let going = null;
            if (attendeeUser) {
                const going_declined = attendeeUser?.responseStatus === "declined";
                const going_accepted = attendeeUser?.responseStatus === "accepted";
                if (going_declined) going = false;
                if (going_accepted) going = true;
            } else if (googleEvent.organizer?.self) {
                going = true;
            }

            return {
                ...basicData,
                title: googleEvent.summary,
                eventColor: getEventColor(googleEvent)?.background || googleCalendar.backgroundColor,
                place: googleEvent.location,
                description: googleEvent.description,
                guestPermissionsModify: getAttributeValue(googleEvent, "guestsCanModify", DEFAULT_PERMISSIONS.GUESTS_CAN_MODIFY),
                guestPermissionsInviteOthers: getAttributeValue(
                    googleEvent,
                    "guestsCanInviteOthers",
                    DEFAULT_PERMISSIONS.GUESTS_CAN_INVITE_OTHERS
                ),
                guestPermissionsSeeGuestList: getAttributeValue(
                    googleEvent,
                    "guestsCanSeeOtherGuests",
                    DEFAULT_PERMISSIONS.GUESTS_CAN_SEE_OTHER_GUESTS
                ),
                guests: googleEvent.attendees?.map((attendee) => ({
                    id: attendee.email,
                    guestEmail: attendee.email,
                    going: attendee.responseStatus === "needsAction" ? null : attendee.responseStatus === "accepted",
                    note: attendee.comment,
                    isOrganizer: attendee.organizer,
                })),
                ownerEmail: googleEvent.creator?.email,
                moduleCode: googleEvent.extendedProperties?.shared?.moduleCode,
                fileCaseId: googleEvent.extendedProperties?.shared?.fileCaseId,
                dependsOnField: googleEvent.extendedProperties?.shared?.dependsOnField,
                organizer: googleEvent.organizer
                    ? {
                          displayName: googleEvent.organizer.displayName,
                          email: googleEvent.organizer.email,
                      }
                    : googleEvent.creator
                    ? {
                          email: googleEvent.creator.email,
                      }
                    : undefined,
                going: going,
                ownerUserId: users.find((x) => x.email === googleEvent.creator?.email)?.id,
                notificationInterval: intervalInBestUnit.interval,
                notificationTimeUnit: intervalInBestUnit.unitOfTime,
            };
        },
        new: (start, end) => {
            return {
                id: null,
                title: "",
                allDay: false,
                start,
                end,
                place: "",
                description: "",
                guestPermissionsModify: DEFAULT_PERMISSIONS.GUESTS_CAN_MODIFY,
                guestPermissionsInviteOthers: DEFAULT_PERMISSIONS.GUESTS_CAN_INVITE_OTHERS,
                guestPermissionsSeeGuestList: DEFAULT_PERMISSIONS.GUESTS_CAN_SEE_OTHER_GUESTS,
                internalGuests: [user],
                externalEmail_Temp: "",
                externalGuestEmails: undefined,
                eventColor: primaryCalendar.backgroundColor,
                notificationInterval: defaultEmailReminderMinutes,
                notificationTimeUnit: "m",
                calendar: primaryCalendar,
            };
        },
        styleGetter: (event, start, end, isSelected) => {
            const { eventColor, id, going, calendar } = event;
            const WHITE_COLOR = "white";
            const isNewEvent = !Boolean(id);

            const backgroundColor = isNewEvent ? calendar.backgroundColor : going ? eventColor : WHITE_COLOR;
            const borderColor = isNewEvent ? WHITE_COLOR : going ? (isSelected ? eventColor : WHITE_COLOR) : eventColor;
            const fontColor = isNewEvent ? WHITE_COLOR : going ? WHITE_COLOR : eventColor;

            const style = {
                backgroundColor,
                borderRadius: "5px",
                border: `1px solid ${borderColor}`,
                color: fontColor,
                display: "block",
                boxShadow:
                    isNewEvent || isSelected
                        ? "0px 6px 10px 0px rgb(0 0 0 / 14%), 0px 1px 18px 0px rgb(0 0 0 / 12%), 0px 3px 5px -1px rgb(0 0 0 / 20%)"
                        : "none",
                textDecoration: going === false && "line-through",
                borderLeft: `5px solid ${calendar.backgroundColor}`,
            };
            return {
                style,
            };
        },
        toGoogleEvent: (event) => {
            const start = event.startDateTime;
            const end = event.endDateTime;
            const qtyMinutesNotification = getDuration(event.notificationInterval, event.notificationTimeUnit).asMinutes();

            const newGoogleEvent = {
                summary: event.title,
                description: event.description,
                location: event.place,
                colorId: Object.keys(EVENT_COLORS).find((key) => EVENT_COLORS[key].background === event.eventColor),
                start: event.allDay ? { date: dateToString(start, false, "yyyy-MM-DD") } : { dateTime: start.toISOString() },
                end: event.allDay ? { date: dateToString(end, false, "yyyy-MM-DD") } : { dateTime: end.toISOString() },
                extendedProperties: {
                    shared: {
                        moduleCode: event.moduleCode,
                        fileCaseId: event.fileCaseId,
                        dependsOnField: event.dependsOnField,
                    },
                },
                guestsCanInviteOthers: Boolean(event.guestPermissionsInviteOthers),
                guestsCanModify: Boolean(event.guestPermissionsModify),
                guestsCanSeeOtherGuests: Boolean(event.guestPermissionsSeeGuestList),
            };

            return {
                forCreate: () => ({
                    ...newGoogleEvent,
                    transparency: "transparent",
                    visibility: "private",
                    source: {
                        url: window.location.href,
                        title: "Case Management Calendar",
                    },
                    eventType: "default",
                    anyoneCanAddSelf: false,
                    attendees: (event.guestEmails || []).map((guestEmail) => ({
                        email: guestEmail,
                        responseStatus: "needsAction",
                        optional: false,
                    })),
                    reminders: {
                        useDefault: false,
                        overrides: [
                            {
                                method: "email",
                                minutes: qtyMinutesNotification,
                            },
                        ],
                    },
                }),
                forUpdate: (googleEventToUpdate) => {
                    const attendees = (event.guestEmails || []).map((guestEmail) => {
                        const attendee = (googleEventToUpdate.attendees || []).find((attendee) => attendee.email === guestEmail);
                        return attendee ? attendee : { email: guestEmail, responseStatus: "needsAction", optional: false };
                    });

                    let reminders;
                    if (googleEventToUpdate.reminders?.useDefault) {
                        reminders = {
                            useDefault: true,
                        };
                    } else {
                        const reminderOverridesExceptEmail = googleEventToUpdate.reminders.overrides.filter(
                            (reminder) => reminder.method !== "email"
                        );
                        reminders = {
                            ...googleEventToUpdate.reminders,
                            overrides: [
                                ...reminderOverridesExceptEmail,
                                {
                                    method: "email",
                                    minutes: qtyMinutesNotification,
                                },
                            ],
                        };
                    }

                    return {
                        ...googleEventToUpdate,
                        ...newGoogleEvent,
                        attendees,
                        reminders,
                    };
                },
            };
        },
        getColorOptions: () => {
            const colorOptions = Object.keys(EVENT_COLORS).map((key) => ({
                value: EVENT_COLORS[key].background,
                label: "",
            }));
            colorOptions.unshift({
                value: primaryCalendar.backgroundColor,
                label: "Calendar",
            });
            return colorOptions;
        },
        getColorForGuestCalendar: () => {
            const notInUseCalendarColorId = Object.keys(EVENT_COLORS).find(
                (key) => !inUseCalendarColors.includes(EVENT_COLORS[key].background)
            );
            const notInUseCalendarColor = notInUseCalendarColorId ? EVENT_COLORS[notInUseCalendarColorId] : null;
            if (notInUseCalendarColor) {
                setInUseCalendarColors((prev) => [...prev, notInUseCalendarColor.background]);
                return notInUseCalendarColor.background;
            }
            const randomCalendarColor = EVENT_COLORS[Math.floor(Math.random() * EVENT_COLORS.length)];
            return randomCalendarColor.background;
        },
        getEventAccessLevel: (event) => {
            const { accessRole } = event;
            const isFreeBusyReader = accessRole === "freeBusyReader";
            const isReader = accessRole === "reader";
            const isWritter = accessRole === "writer" || accessRole === "owner";

            return {
                isFreeBusyReader,
                isReader,
                isWritter,
            };
        },
        amIGuest: (event) => {
            const { guests } = event;
            if (hasElements(guests)) {
                return guests.some((x) => x.id === user.email);
            }
            return false;
        },
        getEventCalendarName: (event, mode) => {
            if (mode === "add")
                return primaryCalendar.summary;

            return event.calendar.summary;
        }
    };
};
